import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useStyles } from './style';
import { Helmet } from 'react-helmet';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Breadcrumbs,
  Typography,
  IconButton,
  Grid,
  Tooltip,
  Box,
} from '@mui/material';
import { getLogFeaturePages } from './services';
import { Icons, LoadingPage } from 'components';
import { isAvailable, whereIsAvailable, isBIReport } from 'utility';
import { useNavigate, Link } from 'react-router-dom';

const PageContainer = (props) => {
  const {
    className,
    isDisableAll = false,
    style,
    children,
    title,
    subTitle,
    breadcrumbs,
    description,
    meta,
    actions,
    onBackClick,
    toolTipText = 'گزارش جاری همراه با تاخیر به‌روزرسانی می‌شود.',
    spaceActions = false,
    loading = false,
    pageKey = null,
    ...rest
  } = props;

  const classes = useStyles();
  const navigate = useNavigate();
  const availableEnvs = whereIsAvailable(pageKey);

  const featureReverseMap = {
    'تایید یا ارتقای کسب‌وکار': 'BUSINESS',
    'مدیریت کسب‌و‌کار': 'BUSINESS',
    'عملیات مالی و بانکی': 'FINANCIAL',
    'خدمات تنخواه گردانی': 'IMPREST',
    'ورود یکپارچه': 'CLIENTS',
    'فروش و مشتریان': 'SALE',
    پشتیبانی: 'SUPPORT',
    'سوالات متداول': 'FAQ',
  };

  const getFeatureKey = (title) => {
    const cleanTitle = normalize(title);
    const key = Object.keys(featureReverseMap).find((k) => normalize(k) === cleanTitle);
    return featureReverseMap[key];
  };

  const normalize = (str) => str?.replace(/\s+/g, ' ').replace(/‌/g, '').replace(/ـ/g, '').trim();

  const getLogPage = async (breadcrumbs) => {
    const key = getFeatureKey(breadcrumbs[0].title);
    const breadcrumbStr = breadcrumbs.slice(1).map((item) => item.title).join(' / ');
    if (!key) {
      return;
    }
    const body = {
      feature: key,
      subFeature: breadcrumbStr,
    };

    try {
      await getLogFeaturePages(body, {
        noToast: true,
      });
    } catch (error) {
      console.warn('LogPage failed silently:', error);
    }
  };
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    if (!breadcrumbs || hasLoggedRef.current) return;

    const timer = setTimeout(() => {
      getLogPage(breadcrumbs);
      hasLoggedRef.current = true;
    }, 15000); // 30 seconds

    return () => clearTimeout(timer);
  }, [breadcrumbs]);

  return (
    <div {...rest} className={clsx(classes.root, className)} style={style}>
      <Helmet>
        <title>{title ? `پنل کسب‌وکار پاد :: ${title}` : 'پنل کسب‌وکار پاد'}</title>
      </Helmet>
      {breadcrumbs ? (
        <Box style={{ display: 'flex' }}>
          <Box className={classes.boxBreadCrumbs}>
            <Icons icon="breadcrumb" color="#667585" hover="#667585" size="17" />
          </Box>
          <Breadcrumbs
            className={classes.breadcrumbs}
            separator={<Icons icon="CaretLeft" color="#667585" size="22" />}
            aria-label="breadcrumb"
          >
            {breadcrumbs.map((v, k) =>
              k !== breadcrumbs.length - 1 ? (
                v?.link ? (
                  <Link key={k} className={classes.breadcrumbsColor} to={v?.link}>
                    {v?.title}
                  </Link>
                ) : (
                  <Typography key={k} className={classes.breadcrumbsColor}>
                    {v?.title}
                  </Typography>
                )
              ) : (
                <Typography key={k} className={classes.breadcrumbsActivePage}>
                  {v.title}
                </Typography>
              ),
            )}
          </Breadcrumbs>
        </Box>
      ) : null}

      <Card className={classes.cardBox}>
        <CardHeader
          avatar={
            onBackClick ? (
              <IconButton onClick={onBackClick} size="large">
                <Icons icon="CaretRight" color="#cfd8dc" hover="#667585" size="40" />
              </IconButton>
            ) : null
          }
          className={classes.cardHeader}
          action={meta}
          title={
            isBIReport(pageKey) ? (
              <>
                {title}{' '}
                <Tooltip title={toolTipText}>
                  <div className={classes.BIBadge}>
                    <Icons icon="Info" color="#667585" size="17" className={classes.BIIcon} />
                    <span> گزارش همراه با تاخیر</span>
                  </div>
                </Tooltip>
              </>
            ) : (
              title
            )
          }
          titleTypographyProps={{ className: classes.title }}
          subheaderTypographyProps={{ className: classes.subTitle }}
          subheader={description || subTitle}
        />
        <CardContent className={isDisableAll && classes.customContent}>
          {isDisableAll && <div className={classes.customDisableLayout}></div>}
          {loading ? (
            <LoadingPage />
          ) : !pageKey || isAvailable(pageKey) ? (
            children
          ) : (
            <Grid container justifyContent="center" alignItems="center" direction="column">
              <img
                src="./images/environment-unavailable.png"
                alt="فعال نبودن گزارش در این محیط"
                className={classes.envUnavailableImg}
              />
              <Typography className={classes.envTitle} variant="h2">
                گزارش جاری
                <span>{' در این محیط '}</span>
                قابل دسترس نیست!
              </Typography>
              <Typography className={classes.envSubTitle} variant="subtitle2">
                پیشنهاد می‌کنیم
                {availableEnvs.length > 0 ? (
                  <>
                    {' '}
                    به
                    <a onClick={() => window.open(availableEnvs[0].url)}>
                      {' محیط '}
                      {availableEnvs[0]?.title}{' '}
                    </a>
                    مراجعه کنید،{' '}
                  </>
                ) : null}
                به
                <a onClick={() => navigate(-1)}> صفحه قبل </a>
                برگردید و یا
                <a onClick={() => navigate('/')}> داشبورد </a>
                را مشاهده کنید.
              </Typography>
            </Grid>
          )}
        </CardContent>
        {actions ? <CardActions disableSpacing={spaceActions}>{actions}</CardActions> : null}
      </Card>
    </div>
  );
};

PageContainer.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.any,
  title: PropTypes.any,
  subTitle: PropTypes.any,
  meta: PropTypes.any,
};

export default PageContainer;
